import { Contract, JsonRpcProvider, formatUnits, MaxUint256 } from 'ethers';
import { BSC_RPC, NPM_BY_DEX } from './lpAutoMint';

const ERC20_ABI = [
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
];

const POOL_ABI = [
  'function token0() view returns (address)',
  'function token1() view returns (address)',
  'function fee() view returns (uint24)',
  'function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint32 feeProtocol, bool unlocked)',
  'function liquidity() view returns (uint128)',
];

const NPM_READ_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)',
  'function positions(uint256 tokenId) view returns (uint96 nonce, address operator, address token0, address token1, uint24 fee, int24 tickLower, int24 tickUpper, uint128 liquidity, uint256 feeGrowthInside0LastX128, uint256 feeGrowthInside1LastX128, uint128 tokensOwed0, uint128 tokensOwed1)',
  'function factory() view returns (address)',
  'function collect((uint256 tokenId,address recipient,uint128 amount0Max,uint128 amount1Max)) payable returns (uint256 amount0, uint256 amount1)',
];

const FACTORY_ABI = [
  'function getPool(address tokenA, address tokenB, uint24 fee) view returns (address pool)',
];

const Q96 = 2n ** 96n;
const MAX_UINT128 = 2n ** 128n - 1n;

function normalizeAddress(input) {
  const raw = String(input || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(raw)) return '';
  return raw.toLowerCase();
}

function tickToSqrtPriceX96(tick) {
  // Uniswap-style approximation using Math.pow then convert; good enough for UI valuation
  const ratio = Math.pow(1.0001, Number(tick));
  const sqrt = Math.sqrt(ratio);
  // sqrtPriceX96 = sqrt(price) * 2^96
  return BigInt(Math.floor(sqrt * Number(Q96)));
}

function getAmount0ForLiquidity(sqrtA, sqrtB, liquidity) {
  let sa = sqrtA;
  let sb = sqrtB;
  if (sa > sb) [sa, sb] = [sb, sa];
  if (sa <= 0n || sb <= 0n || liquidity <= 0n) return 0n;
  return (liquidity * Q96 * (sb - sa)) / sb / sa;
}

function getAmount1ForLiquidity(sqrtA, sqrtB, liquidity) {
  let sa = sqrtA;
  let sb = sqrtB;
  if (sa > sb) [sa, sb] = [sb, sa];
  if (liquidity <= 0n) return 0n;
  return (liquidity * (sb - sa)) / Q96;
}

function amountsForPosition(liquidity, tickLower, tickUpper, tickCurrent) {
  const L = BigInt(liquidity);
  if (L === 0n) return { amount0: 0n, amount1: 0n };
  const sqrtLower = tickToSqrtPriceX96(tickLower);
  const sqrtUpper = tickToSqrtPriceX96(tickUpper);
  const sqrtCurrent = tickToSqrtPriceX96(tickCurrent);

  if (tickCurrent < tickLower) {
    return {
      amount0: getAmount0ForLiquidity(sqrtLower, sqrtUpper, L),
      amount1: 0n,
    };
  }
  if (tickCurrent >= tickUpper) {
    return {
      amount0: 0n,
      amount1: getAmount1ForLiquidity(sqrtLower, sqrtUpper, L),
    };
  }
  return {
    amount0: getAmount0ForLiquidity(sqrtCurrent, sqrtUpper, L),
    amount1: getAmount1ForLiquidity(sqrtLower, sqrtCurrent, L),
  };
}

async function mapLimit(items, limit, worker) {
  const out = [];
  let i = 0;
  async function run() {
    while (i < items.length) {
      const idx = i++;
      out[idx] = await worker(items[idx], idx);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => run()));
  return out;
}

async function readNpmTokenIds(npm, owner) {
  const balance = Number(await npm.balanceOf(owner));
  if (!balance) return [];
  const ids = [];
  // 最多读 40 个仓位，避免一次打爆 RPC
  const n = Math.min(balance, 40);
  for (let i = 0; i < n; i += 1) {
    ids.push(await npm.tokenOfOwnerByIndex(owner, i));
  }
  return ids;
}

async function enrichOnePosition({
  provider,
  npm,
  npmAddress,
  dex,
  tokenId,
  owner,
  filterToken,
}) {
  const raw = await npm.positions(tokenId);
  const liquidity = BigInt(raw.liquidity ?? raw[7] ?? 0n);
  if (liquidity === 0n && BigInt(raw.tokensOwed0 ?? raw[10] ?? 0n) === 0n && BigInt(raw.tokensOwed1 ?? raw[11] ?? 0n) === 0n) {
    return null; // 已清空
  }

  const token0 = String(raw.token0 ?? raw[2]).toLowerCase();
  const token1 = String(raw.token1 ?? raw[3]).toLowerCase();
  const fee = Number(raw.fee ?? raw[4]);
  const tickLower = Number(raw.tickLower ?? raw[5]);
  const tickUpper = Number(raw.tickUpper ?? raw[6]);

  if (filterToken) {
    const f = filterToken.toLowerCase();
    if (token0 !== f && token1 !== f) return null;
  }

  const factoryAddr = await npm.factory();
  const factory = new Contract(factoryAddr, FACTORY_ABI, provider);
  const poolAddr = await factory.getPool(token0, token1, fee);
  if (!poolAddr || poolAddr === '0x0000000000000000000000000000000000000000') return null;

  const pool = new Contract(poolAddr, POOL_ABI, provider);
  const token0C = new Contract(token0, ERC20_ABI, provider);
  const token1C = new Contract(token1, ERC20_ABI, provider);

  const [slot0, dec0, dec1, sym0, sym1] = await Promise.all([
    pool.slot0(),
    token0C.decimals(),
    token1C.decimals(),
    token0C.symbol().catch(() => 'T0'),
    token1C.symbol().catch(() => 'T1'),
  ]);
  const tickCurrent = Number(slot0.tick);
  const { amount0, amount1 } = amountsForPosition(liquidity, tickLower, tickUpper, tickCurrent);

  // eth_call 模拟 collect，读取可领取手续费（含未写入 tokensOwed 的部分）
  let fee0 = 0n;
  let fee1 = 0n;
  try {
    const collected = await npm.collect.staticCall({
      tokenId,
      recipient: owner,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128,
    });
    fee0 = BigInt(collected.amount0 ?? collected[0] ?? 0n);
    fee1 = BigInt(collected.amount1 ?? collected[1] ?? 0n);
  } catch (_) {
    fee0 = BigInt(raw.tokensOwed0 ?? raw[10] ?? 0n);
    fee1 = BigInt(raw.tokensOwed1 ?? raw[11] ?? 0n);
  }

  let inRange = tickCurrent >= tickLower && tickCurrent < tickUpper;
  let status = inRange ? 'earning' : (tickCurrent < tickLower ? 'below' : 'above');
  let statusText = inRange
    ? '在区间内 · 正在累计手续费'
    : (tickCurrent < tickLower
      ? '低于区间 · 仓位偏 token0，基本停收手续费'
      : '高于区间 · 仓位偏 token1，基本停收手续费');

  return {
    id: `${dex}-${tokenId.toString()}`,
    dex,
    dexName: dex === 'uniswap' ? 'Uniswap' : 'PancakeSwap',
    npm: npmAddress,
    tokenId: tokenId.toString(),
    pool: poolAddr,
    token0,
    token1,
    symbol0: sym0,
    symbol1: sym1,
    pairLabel: `${sym0}/${sym1}`,
    fee,
    feePct: fee / 10000,
    tickLower,
    tickUpper,
    tickCurrent,
    liquidity: liquidity.toString(),
    amount0: amount0.toString(),
    amount1: amount1.toString(),
    amount0Human: Number(formatUnits(amount0, Number(dec0))),
    amount1Human: Number(formatUnits(amount1, Number(dec1))),
    fee0: fee0.toString(),
    fee1: fee1.toString(),
    fee0Human: Number(formatUnits(fee0, Number(dec0))),
    fee1Human: Number(formatUnits(fee1, Number(dec1))),
    decimals0: Number(dec0),
    decimals1: Number(dec1),
    inRange,
    status,
    statusText,
    owner,
  };
}

/**
 * 读取地址在 Pancake / Uniswap V3 PositionManager 上的真实仓位与可领手续费
 */
export async function fetchOnchainV3Positions({
  owner,
  filterToken = '',
  rpcUrl = BSC_RPC,
  dexes = ['pancake', 'uniswap'],
}) {
  const address = normalizeAddress(owner);
  if (!address) throw new Error('请输入有效钱包地址');

  const provider = new JsonRpcProvider(rpcUrl, 56);
  const all = [];

  for (const dex of dexes) {
    const npmAddress = NPM_BY_DEX[dex];
    if (!npmAddress) continue;
    const npm = new Contract(npmAddress, NPM_READ_ABI, provider);
    let tokenIds = [];
    try {
      tokenIds = await readNpmTokenIds(npm, address);
    } catch (err) {
      // 某些节点对 enumerable 支持差，跳过该 DEX
      continue;
    }
    const rows = await mapLimit(tokenIds, 4, async (tokenId) => {
      try {
        return await enrichOnePosition({
          provider,
          npm,
          npmAddress,
          dex,
          tokenId,
          owner: address,
          filterToken: normalizeAddress(filterToken),
        });
      } catch {
        return null;
      }
    });
    all.push(...rows.filter(Boolean));
  }

  return {
    owner: address,
    syncedAt: Date.now(),
    positions: all,
  };
}

export function estimatePositionUsd(pos, priceByAddress = {}) {
  const p0 = Number(priceByAddress[pos.token0] ?? 0);
  const p1 = Number(priceByAddress[pos.token1] ?? 0);
  // 稳定币近似 $1
  const stable = new Set(['USDT', 'USDC', 'USD1', 'BUSD', 'DAI']);
  const px0 = p0 > 0 ? p0 : (stable.has(String(pos.symbol0 || '').toUpperCase()) ? 1 : 0);
  const px1 = p1 > 0 ? p1 : (stable.has(String(pos.symbol1 || '').toUpperCase()) ? 1 : 0);
  const principalUsd = (pos.amount0Human || 0) * px0 + (pos.amount1Human || 0) * px1;
  const feesUsd = (pos.fee0Human || 0) * px0 + (pos.fee1Human || 0) * px1;
  return { principalUsd, feesUsd, totalUsd: principalUsd + feesUsd, px0, px1 };
}
